/**
 * operationEvents.js — RC 1.53 Phase 4.1
 *
 * Browser-native EventTarget bus for the OperationLifecycleController.
 * No dependency on any external state library. One singleton per tab.
 *
 * Events (CustomEvent.detail shape):
 *
 *   operation:start    { jobId, actionCode, runId|null, idempotencyKey|null }
 *   operation:progress { jobId, actionCode, runId, status, progressLabel|null }
 *   operation:complete { jobId, actionCode, runId, envelope }
 *   operation:fail     { jobId, actionCode, runId|null, error }
 *   operation:abort    { jobId, actionCode, runId|null, reason }
 *
 * Phase 4.1 contract: this module exposes a singleton bus + a thin
 * `emit` / `subscribe` API. It does NOT do any business logic; it is
 * a transport only. Phase 4.2+ will wire adapters and section
 * subscribers through this bus.
 */

const _bus = new EventTarget();

export const OPERATION_EVENT_TYPES = Object.freeze({
  START:    'operation:start',
  PROGRESS: 'operation:progress',
  // Phase -1 Tier 1 — S4 — emitted by `attachExistingRun` BEFORE the
  // first poll fires. Subscribers (Activity Centre + section UI)
  // render the "Reconnecting to in-flight run…" copy instead of the
  // "Generating…" copy and do NOT disable user-initiated Generate
  // buttons. The op subsequently transitions to POLLING when the
  // first heartbeat lands.
  RECOVERING: 'operation:recovering',
  // Phase -1 Tier 1 — S4 — emitted when a transient network blip
  // is detected during POLLING. Adapter / poller decides whether
  // to back off + retry. Permit is still reserved.
  RECOVERABLE_STALL: 'operation:recoverable_stall',
  // RC 1.53 Phase 4.2.1.1 — emitted when the adapter's `run`
  // callback resolves with a terminal envelope. The operation is
  // now in STABILIZING (post-success work in flight: finalize-
  // charge, hydration verifier, credits refresh, cache
  // invalidation). The activity indicator stays mounted until
  // `markComplete` is called.
  STABILIZING: 'operation:stabilizing',
  COMPLETE: 'operation:complete',
  FAIL:     'operation:fail',
  ABORT:    'operation:abort',
  // Phase 4.2 — cross-section signal emitted by the controller's
  // adapter (or by the section, on finalize-charge success) when a
  // chargeable artefact for the job has reached terminal-success and
  // is ready for downstream consumers (e.g. PackSection re-deriving
  // the "Application pack available" gate, Documents library cache
  // invalidation). NOT consumed yet in 4.2 — Phase 4.3 wires the
  // PackSection listener.
  PACK_ARTEFACT_READY: 'pack:artefact_ready',
});

/**
 * Emit a controller event. Detail must be a plain serialisable object.
 * Subscribers receive the event via `subscribe(type, handler)`.
 */
export function emit(type, detail) {
  if (!type || typeof type !== 'string') {
    throw new Error('emit: type is required');
  }
  if (!Object.values(OPERATION_EVENT_TYPES).includes(type)) {
    // Defensive: prevent silent typos.
    throw new Error(`emit: unknown event type '${type}'`);
  }
  _bus.dispatchEvent(new CustomEvent(type, { detail: detail || {} }));
}

/**
 * Subscribe to a controller event. Returns an unsubscribe function.
 */
export function subscribe(type, handler) {
  if (!type || typeof type !== 'string') {
    throw new Error('subscribe: type is required');
  }
  if (typeof handler !== 'function') {
    throw new Error('subscribe: handler must be a function');
  }
  const wrapped = (ev) => handler(ev.detail);
  _bus.addEventListener(type, wrapped);
  return () => _bus.removeEventListener(type, wrapped);
}

/** Test-only — reset the bus. Never invoke from production code. */
export function _resetBusForTests() {
  // EventTarget has no public clear; subscribers should track their
  // own unsubscribe handles. This helper is a no-op placeholder so
  // tests can document the intent. Subscribers added via
  // `subscribe(...)` return their own unsubscribe.
  /* no-op */
}
